import { useEffect, useMemo, useState } from "react";
import { Redirect, useHistory, useLocation, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import { Alert } from "reactstrap";
import { useTranslation } from "react-i18next";
import PageHeader from "../../components/PageHeader";
import Loading from "../../components/utils/Loading";
import ForbiddenModule from "../../components/ForbiddenModule";
import SettingsWizardFooter from "../settings/SettingsWizardFooter";
import { WASTE_FORM_PANEL_WIDTH_PX } from "../settings/waste/wasteFlowFormUi";
import {
  addCampaign,
  getCampaigns,
  updateCampaign,
} from "../../services/notification";
import CampaignGeneralInfoCard from "./components/CampaignGeneralInfoCard";
import CampaignTrackedMaterialsSection from "./components/CampaignTrackedMaterialsSection";
import { useCampaignFormData } from "./hooks/useCampaignFormData";
import {
  buildDefaultCampaignPeriod,
  buildCampaignPrefillFromFlux,
  toCampaignFormModel,
} from "./campaignDuplicateUtils";

const SINCE_BEGINNING_START = new Date(2021, 3, 26);

const getInitialFormData = (locationState, isEditMode) => {
  if (isEditMode && locationState?.campaign) {
    return toCampaignFormModel(locationState.campaign);
  }
  if (!isEditMode && locationState?.duplicateTemplate) {
    return locationState.duplicateTemplate;
  }
  if (!isEditMode && locationState?.createPrefill) {
    return locationState.createPrefill;
  }
  return null;
};

const toIsoDate = (dateValue, endOfDay = false) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  if (endOfDay) {
    date.setHours(23, 59, 59, 999);
  } else {
    date.setHours(0, 0, 0, 0);
  }
  return date.toISOString();
};

const normalizeCampaignFormSnapshot = ({
  start,
  stop,
  addressId,
  containerObjectIdList,
  trackedMaterials,
}) => ({
  start: start || "",
  stop: stop || "",
  addressId: String(addressId || ""),
  containerObjectIdList: [...(containerObjectIdList || [])].map(String).sort(),
  trackedMaterials: [...(trackedMaterials || [])].map(String).sort(),
});

const CampaignCreate = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { id: campaignIdFromRoute } = useParams();
  const token = Cookies.get("userToken");
  const isEditMode = Boolean(campaignIdFromRoute);
  const duplicateTemplate = !isEditMode
    ? location.state?.duplicateTemplate ?? null
    : null;
  const isDuplicateMode = Boolean(duplicateTemplate);
  const initialFormData = getInitialFormData(location.state, isEditMode);
  const [editingCampaign, setEditingCampaign] = useState(() =>
    isEditMode ? initialFormData : null,
  );
  const [isLoadingCampaign, setIsLoadingCampaign] = useState(
    () => isEditMode && !initialFormData,
  );

  const corporationModules = useMemo(() => {
    const stored = localStorage.getItem("corporationModules");
    return stored ? JSON.parse(stored) : null;
  }, []);

  const materialList = useMemo(() => {
    const stored = localStorage.getItem("materialsDescription");
    return stored
      ? JSON.parse(stored).filter((item) => item.isActiveSortingError)
      : [];
  }, []);

  const defaultCampaignPeriod = useMemo(() => buildDefaultCampaignPeriod(), []);
  const [campaignAllowed, setCampaignAllowed] = useState(true);
  const [periodCampaignStart, setPeriodCampaignStart] = useState(
    () => initialFormData?.start || defaultCampaignPeriod.start,
  );
  const [periodCampaignStop, setPeriodCampaignStop] = useState(
    () => initialFormData?.stop || defaultCampaignPeriod.stop,
  );
  const [campaignAddress, setCampaignAddress] = useState(
    () => initialFormData?.addressId || "",
  );
  const [containerObjectIdList, setContainerObjectIdList] = useState(
    () => initialFormData?.containerObjectIdList || [],
  );
  const [trackedMaterials, setTrackedMaterials] = useState(
    () => initialFormData?.trackedMaterials || [],
  );
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const viewMode = Cookies.get("viewMode") || "client";
  const isAkanthasAccount = (Cookies.get("userEmail") || "").includes(
    "@akanthas.com",
  );

  const { addressList, containerList, filterContainerByAddress, isLoading } =
    useCampaignFormData();

  const fromBilans = !isEditMode ? location.state?.fromBilans ?? null : null;
  const [bilansPrefillApplied, setBilansPrefillApplied] = useState(false);

  const containersForAddress = useMemo(
    () => filterContainerByAddress(campaignAddress),
    [filterContainerByAddress, campaignAddress],
  );

  const isFormValid =
    Boolean(periodCampaignStart) &&
    Boolean(periodCampaignStop) &&
    Boolean(campaignAddress) &&
    containerObjectIdList.length > 0 &&
    trackedMaterials.length > 0 &&
    new Date(periodCampaignStart) <= new Date(periodCampaignStop);

  const isFormReady =
    !isLoading &&
    !isLoadingCampaign &&
    (!isEditMode || Boolean(editingCampaign));

  const baselineForm = useMemo(() => {
    if (!isEditMode || !editingCampaign) {
      return null;
    }
    return normalizeCampaignFormSnapshot(editingCampaign);
  }, [isEditMode, editingCampaign]);

  const currentFormSnapshot = useMemo(
    () =>
      normalizeCampaignFormSnapshot({
        start: periodCampaignStart,
        stop: periodCampaignStop,
        addressId: campaignAddress,
        containerObjectIdList,
        trackedMaterials,
      }),
    [
      periodCampaignStart,
      periodCampaignStop,
      campaignAddress,
      containerObjectIdList,
      trackedMaterials,
    ],
  );

  const hasUnsavedChanges = useMemo(() => {
    if (!isEditMode || !baselineForm || !isFormReady) {
      return false;
    }
    return JSON.stringify(currentFormSnapshot) !== JSON.stringify(baselineForm);
  }, [isEditMode, baselineForm, currentFormSnapshot, isFormReady]);

  const showFooter = !isEditMode || hasUnsavedChanges;

  useEffect(() => {
    if (corporationModules !== null) {
      setCampaignAllowed(corporationModules?.showCampaignModule || false);
    }
  }, [corporationModules]);

  useEffect(() => {
    if (!editingCampaign) {
      return;
    }

    setPeriodCampaignStart(editingCampaign.start);
    setPeriodCampaignStop(editingCampaign.stop);
    setCampaignAddress(editingCampaign.addressId || "");
    setContainerObjectIdList(editingCampaign.containerObjectIdList || []);
    setTrackedMaterials(editingCampaign.trackedMaterials || []);
  }, [editingCampaign]);

  useEffect(() => {
    if (!isEditMode || editingCampaign) {
      return;
    }

    let cancelled = false;

    const loadCampaign = async () => {
      setIsLoadingCampaign(true);
      try {
        const now = new Date();
        now.setHours(23, 59, 59, 999);
        const start = new Date(SINCE_BEGINNING_START);
        start.setHours(0, 0, 0, 0);

        const response = await getCampaigns({
          start: start.toISOString(),
          stop: now.toISOString(),
        });

        const campaigns = response.data.campaigns || [];
        const found = campaigns.find(
          (campaign) => String(campaign._id) === String(campaignIdFromRoute),
        );

        if (cancelled) {
          return;
        }

        if (found) {
          setEditingCampaign(toCampaignFormModel(found));
        } else {
          history.replace("/campaign");
        }
      } catch (err) {
        console.error("Error loading campaign:", err);
        if (!cancelled) {
          history.replace("/campaign");
        }
      } finally {
        if (!cancelled) {
          setIsLoadingCampaign(false);
        }
      }
    };

    loadCampaign();

    return () => {
      cancelled = true;
    };
  }, [campaignIdFromRoute, editingCampaign, history, isEditMode]);

  useEffect(() => {
    if (
      !campaignAddress &&
      addressList.length > 0 &&
      !initialFormData &&
      !fromBilans
    ) {
      setCampaignAddress(addressList[0]._id);
    }
  }, [addressList, campaignAddress, initialFormData, fromBilans]);

  useEffect(() => {
    if (!fromBilans || bilansPrefillApplied || isLoading || isEditMode) {
      return;
    }
    if (!containerList.length && !addressList.length) {
      return;
    }

    const prefill = buildCampaignPrefillFromFlux({
      fluxLabel: fromBilans.fluxLabel,
      trackedMaterial: fromBilans.trackedMaterial,
      containerList,
      addressList,
    });

    setPeriodCampaignStart(prefill.start);
    setPeriodCampaignStop(prefill.stop);
    setCampaignAddress(prefill.addressId || addressList[0]?._id || "");
    setContainerObjectIdList(prefill.containerObjectIdList || []);
    setTrackedMaterials(prefill.trackedMaterials || []);
    setBilansPrefillApplied(true);
    history.replace({ pathname: location.pathname, state: {} });
  }, [
    fromBilans,
    bilansPrefillApplied,
    isLoading,
    isEditMode,
    containerList,
    addressList,
    history,
    location.pathname,
  ]);

  useEffect(() => {
    if (containersForAddress.length === 0) {
      return;
    }
    setContainerObjectIdList((prev) =>
      prev.filter((id) => containersForAddress.some((c) => c._id === id)),
    );
  }, [containersForAddress]);

  const handleAddressChange = (addressId) => {
    setCampaignAddress(addressId);
    setContainerObjectIdList([]);
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    if (!isFormValid || isSaving) return;
    if (isEditMode && !editingCampaign?._id && !campaignIdFromRoute) return;

    setError(null);
    setIsSaving(true);

    try {
      const campaignData = {
        start: periodCampaignStart,
        stop: periodCampaignStop,
        addressId: campaignAddress,
        trackedMaterials,
        containerObjectIdList,
      };

      const res = isEditMode
        ? await updateCampaign(
            editingCampaign?._id || campaignIdFromRoute,
            campaignData,
          )
        : await addCampaign(campaignData);

      if (res.status === 200) {
        history.push("/campaign");
      } else {
        setError(t("Error saving campaign. Please try again."));
      }
    } catch (err) {
      console.error("Error creating campaign:", err);
      setError(
        err.response?.data?.message ||
          t("Error saving campaign. Please try again."),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const pageTitle = isEditMode
    ? t("campaign.editTitle")
    : isDuplicateMode
      ? t("campaign.duplicateTitle")
      : t("campaign.createTitle");

  const primaryLabel = isSaving
    ? t("Saving...")
    : isEditMode
      ? t("Save")
      : t("campaign.createSubmit");

  if (!token) {
    return <Redirect to={{ pathname: "/login" }} />;
  }

  if (!campaignAllowed && !(isAkanthasAccount && viewMode === "admin")) {
    return (
      <div className="settings-page-wrapper settings-page-wrapper--wizard">
        <PageHeader
          title={pageTitle}
          showBackButton
          backRoute="/campaign"
          backLabel={t("campaign.backToList")}
        />
        <div className="settings-content-wrapper settings-create-wizard">
          <ForbiddenModule viewType="campaign" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`settings-page-wrapper settings-page-wrapper--wizard settings-page-wrapper--campaign-create${
        showFooter ? "" : " settings-page-wrapper--no-footer"
      }`}
    >
      <PageHeader
        title={pageTitle}
        showBackButton
        backRoute="/campaign"
        backLabel={t("campaign.backToList")}
      />

      <div
        className={`settings-content-wrapper settings-create-wizard settings-create-wizard--campaign${
          showFooter ? "" : " settings-create-wizard--no-footer"
        }`}
      >
        {isLoading || isLoadingCampaign ? (
          <Loading color="#F5F5F5" />
        ) : (
          <div
            className="settings-create-wizard__inner settings-create-wizard__inner--campaign"
            style={{ maxWidth: WASTE_FORM_PANEL_WIDTH_PX }}
          >
            <div className="settings-create-wizard__panel">
              <form
                id="campaign-create-form"
                className="flex flex-col gap-8 w-full"
                onSubmit={handleSubmit}
              >
                {error ? (
                  <Alert color="danger" className="mb-0 w-full">
                    {error}
                  </Alert>
                ) : null}

                <CampaignGeneralInfoCard
                  startDate={periodCampaignStart}
                  endDate={periodCampaignStop}
                  onStartDateChange={(value) =>
                    setPeriodCampaignStart(toIsoDate(value, false))
                  }
                  onEndDateChange={(value) =>
                    setPeriodCampaignStop(toIsoDate(value, true))
                  }
                  addressId={campaignAddress}
                  onAddressChange={handleAddressChange}
                  addressList={addressList}
                  containers={containersForAddress}
                  containerObjectIdList={containerObjectIdList}
                  onContainerChange={setContainerObjectIdList}
                />

                <CampaignTrackedMaterialsSection
                  materialList={materialList}
                  trackedMaterials={trackedMaterials}
                  setTrackedMaterials={setTrackedMaterials}
                  selectedContainers={containersForAddress.filter((container) =>
                    containerObjectIdList.includes(container._id),
                  )}
                />
              </form>
            </div>
          </div>
        )}
      </div>

      {showFooter ? (
        <SettingsWizardFooter
          saving={isSaving}
          onQuit={() => history.push("/campaign")}
          quitLabel={t("Cancel")}
          primaryLabel={primaryLabel}
          onPrimary={handleSubmit}
          primaryDisabled={isSaving || !isFormValid}
          innerMaxWidth={WASTE_FORM_PANEL_WIDTH_PX}
          innerClassName="settings-wizard-footer__inner--form-panel"
        />
      ) : null}
    </div>
  );
};

export default CampaignCreate;
